import { useKeycloak } from '@react-keycloak/web';
import { useUnit } from 'effector-react';
import { keycloakLogout } from '@/config/keycloak';
import { $user } from '@/models/user';
import { GroupsEnum } from '@/types/users';

const HELPDESK_EMAIL = 'helpdesk@famil.ru';
const EMAIL_SUBJECT = 'Доступ к неаудируемым системам';
const SYSTEM_NAME = 'HH';

const formatAccessRole = (group: GroupsEnum) => {
  const roleName = group.split('/').filter(Boolean).pop() || group;

  return roleName.replace(
    /^([^.]+)\.\s*(.)/u,
    (_, system: string, firstLetter: string) =>
      `${system.toLocaleUpperCase('ru-RU')}. ${firstLetter.toLocaleUpperCase('ru-RU')}`,
  );
};

const HH_ACCESS_ROLES = [GroupsEnum.RECRUTER].map(formatAccessRole);
const EMAIL_BODY = `Прошу предоставить доступ к неаудируемой системе "${SYSTEM_NAME}":\n${HH_ACCESS_ROLES.map(
  (role) => `- ${role}`,
).join('\n')}`;

const AccessDenied = () => {
  const { keycloak } = useKeycloak();
  const user = useUnit($user);
  const fullName =
    [user?.family_name, user?.given_name].filter(Boolean).join(' ') ||
    [user?.secondname, user?.firstname].filter(Boolean).join(' ') ||
    user?.name ||
    user?.preferred_username ||
    'Пользователь';
  const userIdentifier = user?.email || user?.preferred_username;
  const userLabel = userIdentifier ? `${fullName} (${userIdentifier})` : fullName;

  return (
    <main className="grid min-h-screen w-full place-items-center bg-gray-100 p-4 sm:p-6">
      <section className="flex w-full max-w-[720px] flex-col items-center rounded-xl border border-gray-200 bg-white p-5 text-center shadow-sm sm:p-10">
        <h1 className="text-xl font-semibold tracking-tight text-gray-950">Доступ запрещён</h1>
        <p className="mt-2.5 max-w-[600px] text-sm leading-5 text-gray-600">
          {userLabel}, у вас недостаточно прав для доступа к этому разделу.
        </p>

        <div className="mt-6 w-full max-w-[620px] rounded-[10px] border border-[#6142751f] bg-[#6142750a] px-4 py-4 text-sm leading-5 text-gray-600 sm:px-6 sm:py-5">
          <p className="mx-auto mb-[18px] max-w-[540px] text-center">
            Для просмотра информации на этой странице вам необходимо направить запрос на{' '}
            <a
              className="whitespace-nowrap font-semibold text-[#614275] underline"
              href={`mailto:${HELPDESK_EMAIL}?subject=${encodeURIComponent(
                EMAIL_SUBJECT,
              )}&body=${encodeURIComponent(EMAIL_BODY)}`}
            >
              {HELPDESK_EMAIL}
            </a>
          </p>

          <div className="grid gap-1 border-t border-[#6142751f] py-3 text-left sm:grid-cols-[110px_minmax(0,1fr)] sm:gap-4">
            <strong className="font-semibold text-gray-950">Тема письма</strong>
            <span>{EMAIL_SUBJECT}</span>
          </div>
          <div className="grid gap-1 border-t border-[#6142751f] py-3 text-left sm:grid-cols-[110px_minmax(0,1fr)] sm:gap-4">
            <strong className="font-semibold text-gray-950">Тело письма</strong>
            <div className="min-w-0">
              <p>Прошу предоставить доступ к неаудируемой системе &quot;{SYSTEM_NAME}&quot;:</p>
              <ul className="mt-2 flex flex-col gap-1.5">
                {HH_ACCESS_ROLES.map((role) => (
                  <li
                    key={role}
                    className="relative rounded-[7px] bg-white py-2 pl-[30px] pr-3 before:absolute before:left-3 before:top-2 before:font-bold before:text-[#614275] before:content-['•']"
                  >
                    {role}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="mt-6 min-h-10 w-full rounded-lg border-0 bg-[#614275] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#513461] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#614275] sm:w-auto"
          onClick={() => keycloakLogout(keycloak)}
        >
          Выйти из системы
        </button>
      </section>
    </main>
  );
};

export default AccessDenied;
